import ButtonBase from '@mui/material/ButtonBase';
import { alpha, styled } from '@mui/material/styles';
import { getZoomAdjustedThemeSpacingPx } from 'components/ui/utilities/theme';
import { getZoomAdjustedPx } from 'components/ui/utilities/zoom';
import { getPrismScrollbarStyles } from 'theme';

interface ItemButtonStyleProps {
  isSelected?: boolean;
}

interface ZoomStyleProps {
  zoomLevel: number;
}

const shouldForwardItemButtonProp = (prop: PropertyKey) => prop !== 'isSelected';
const shouldForwardZoomProp = (prop: PropertyKey) => prop !== 'zoomLevel';

export const EntityListPanelRoot = styled('aside', {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ theme, zoomLevel }) => ({
  '--entity-list-panel-badge-font-size': getZoomAdjustedPx(10, zoomLevel),
  '--entity-list-panel-badge-line-height': getZoomAdjustedPx(14, zoomLevel),
  '--entity-list-panel-border-width': getZoomAdjustedPx(1, zoomLevel),
  '--entity-list-panel-count-size': getZoomAdjustedPx(22, zoomLevel),
  '--entity-list-panel-column-gap': getZoomAdjustedThemeSpacingPx(theme, 's', zoomLevel),
  '--entity-list-panel-filter-height': getZoomAdjustedPx(20, zoomLevel),
  '--entity-list-panel-filter-max-width': getZoomAdjustedPx(172, zoomLevel),
  '--entity-list-panel-focus-ring-width': getZoomAdjustedPx(3, zoomLevel),
  '--entity-list-panel-font-size-l': getZoomAdjustedPx(16, zoomLevel),
  '--entity-list-panel-font-size-m': getZoomAdjustedPx(14, zoomLevel),
  '--entity-list-panel-font-size-s': getZoomAdjustedPx(12, zoomLevel),
  '--entity-list-panel-gap-xs': getZoomAdjustedThemeSpacingPx(theme, 'xs', zoomLevel),
  '--entity-list-panel-icon-size': getZoomAdjustedPx(16, zoomLevel),
  '--entity-list-panel-line-height-l': getZoomAdjustedPx(24, zoomLevel),
  '--entity-list-panel-line-height-m': getZoomAdjustedPx(20, zoomLevel),
  '--entity-list-panel-line-height-s': getZoomAdjustedPx(16, zoomLevel),
  '--entity-list-panel-badge-max-width': getZoomAdjustedPx(128, zoomLevel),
  '--entity-list-panel-meta-max-width': getZoomAdjustedPx(132, zoomLevel),
  '--entity-list-panel-radius-s': getZoomAdjustedPx(theme.shape.radius.s, zoomLevel),
  '--entity-list-panel-radius-xs': getZoomAdjustedPx(theme.shape.radius.xs, zoomLevel),
  '--entity-list-panel-row-height': getZoomAdjustedPx(38, zoomLevel),
  '--prism-scrollbar-thumb-border-color': theme.palette.neutral[100],
  '--prism-scrollbar-thumb-border-width': getZoomAdjustedPx(2, zoomLevel),
  '--prism-scrollbar-width': getZoomAdjustedPx(8, zoomLevel),
  '--entity-list-panel-search-height': getZoomAdjustedPx(26, zoomLevel),
  '--entity-list-panel-spacing-xxs': getZoomAdjustedThemeSpacingPx(theme, 'xxs', zoomLevel),
  '--entity-list-panel-spacing-xs': getZoomAdjustedThemeSpacingPx(theme, 'xs', zoomLevel),
  '--entity-list-panel-spacing-s': getZoomAdjustedThemeSpacingPx(theme, 's', zoomLevel),
  '--entity-list-panel-state-padding-x': getZoomAdjustedThemeSpacingPx(theme, 'm', zoomLevel),
  '--entity-list-panel-state-padding-y': getZoomAdjustedThemeSpacingPx(theme, 'xl', zoomLevel),
  '--scroll-hidden-items-hint-border-width': 'var(--entity-list-panel-border-width)',
  '--scroll-hidden-items-hint-font-size': 'var(--entity-list-panel-font-size-m)',
  '--scroll-hidden-items-hint-gap': 'var(--entity-list-panel-spacing-xxs)',
  '--scroll-hidden-items-hint-icon-size': 'var(--entity-list-panel-font-size-s)',
  '--scroll-hidden-items-hint-line-height': 'var(--entity-list-panel-line-height-m)',
  '--scroll-hidden-items-hint-offset': 'var(--entity-list-panel-spacing-xxs)',
  '--scroll-hidden-items-hint-padding': 'var(--entity-list-panel-spacing-xxs) var(--entity-list-panel-spacing-xs)',
  '--scroll-hidden-items-hint-radius': 'var(--entity-list-panel-radius-xs)',
  backgroundColor: theme.palette.neutral[100],
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--entity-list-panel-gap-xs)',
  height: '100%',
  minHeight: 0,
  minWidth: 0,
  padding: 'var(--entity-list-panel-spacing-s)',
  width: '100%',
}));

export const EntityListPanelHeader = styled('div')({
  alignItems: 'flex-start',
  display: 'flex',
  flexShrink: 0,
  gap: 'var(--entity-list-panel-gap-xs)',
  justifyContent: 'space-between',
  minWidth: 0,
});

export const EntityListPanelTitle = styled('h2')(({ theme }) => ({
  color: theme.palette.text.primary,
  flex: '1 1 auto',
  fontSize: 'var(--entity-list-panel-font-size-l)',
  fontWeight: theme.typography.fontWeightMedium,
  letterSpacing: 0,
  lineHeight: 'var(--entity-list-panel-line-height-l)',
  margin: 0,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const EntityListPanelHeaderActions = styled('div')({
  alignItems: 'center',
  display: 'inline-flex',
  flexShrink: 0,
  minHeight: 'var(--entity-list-panel-line-height-l)',
});

export const EntityListPanelControls = styled('div')({
  display: 'grid',
  flexShrink: 0,
  gap: 'var(--entity-list-panel-gap-xs)',
});

export const EntityListPanelSearch = styled('label')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.neutral[200],
  border: `var(--entity-list-panel-border-width) solid ${theme.palette.neutral[500]}`,
  borderRadius: 'var(--entity-list-panel-radius-xs)',
  color: theme.palette.neutral[500],
  display: 'flex',
  gap: 'var(--entity-list-panel-gap-xs)',
  minHeight: 'var(--entity-list-panel-search-height)',
  padding: 'var(--entity-list-panel-spacing-xxs) var(--entity-list-panel-spacing-s)',
  transition: theme.transitions.create(['border-color', 'box-shadow'], {
    duration: theme.transitions.duration.shortest,
  }),
  '& svg': {
    color: theme.palette.neutral[800],
    fontSize: 'var(--entity-list-panel-icon-size)',
  },
  '&:focus-within': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 var(--entity-list-panel-focus-ring-width) ${alpha(theme.palette.primary.main, 0.18)}`,
  },
}));

export const EntityListPanelSearchInput = styled('input')(({ theme }) => ({
  background: 'transparent',
  border: 0,
  color: theme.palette.neutral[800],
  flex: '1 1 auto',
  font: 'inherit',
  fontSize: 'var(--entity-list-panel-font-size-m)',
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: 'var(--entity-list-panel-line-height-m)',
  minWidth: 0,
  outline: 0,
  padding: 0,
  '&::placeholder': {
    color: theme.palette.neutral[500],
    opacity: 1,
  },
}));

export const EntityListPanelSearchClearButton = styled('button')(({ theme }) => ({
  alignItems: 'center',
  appearance: 'none',
  backgroundColor: 'transparent',
  border: 0,
  borderRadius: '50%',
  color: theme.palette.neutral[600],
  cursor: 'pointer',
  display: 'inline-flex',
  flexShrink: 0,
  height: 'var(--entity-list-panel-icon-size)',
  justifyContent: 'center',
  padding: 0,
  transition: theme.transitions.create(['background-color', 'color'], {
    duration: theme.transitions.duration.shortest,
  }),
  width: 'var(--entity-list-panel-icon-size)',
  '& svg': {
    color: 'inherit',
    fontSize: 'var(--entity-list-panel-icon-size)',
  },
  '&:hover, &:focus-visible': {
    backgroundColor: theme.palette.brand[100],
    color: theme.palette.brand[700],
    outline: 'none',
  },
}));

export const EntityListPanelFilterButton = styled(ButtonBase)(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.neutral[300],
  border: 0,
  borderRadius: 'var(--entity-list-panel-radius-xs)',
  color: theme.palette.neutral[700],
  display: 'inline-flex',
  fontSize: 'var(--entity-list-panel-font-size-s)',
  fontWeight: theme.typography.fontWeightRegular,
  gap: 'var(--entity-list-panel-spacing-xxs)',
  justifyContent: 'space-between',
  lineHeight: 'var(--entity-list-panel-line-height-s)',
  maxWidth: 'var(--entity-list-panel-filter-max-width)',
  minHeight: 'var(--entity-list-panel-filter-height)',
  minWidth: 0,
  overflow: 'hidden',
  padding: 'var(--entity-list-panel-spacing-xxs)',
  textAlign: 'left',
  transition: theme.transitions.create(['background-color', 'border-color', 'color'], {
    duration: theme.transitions.duration.shortest,
  }),
  '&:hover': {
    backgroundColor: theme.palette.neutral[400],
    color: theme.palette.neutral[800],
  },
  '&:focus-visible': {
    backgroundColor: theme.palette.neutral[400],
    color: theme.palette.neutral[800],
    outline: 0,
  },
  '& svg': {
    flexShrink: 0,
  },
}));

export const EntityListPanelFilterButtonLabel = styled('span')({
  alignItems: 'center',
  display: 'block',
  flex: '1 1 auto',
  gap: 'var(--entity-list-panel-gap-xs)',
  maxWidth: '100%',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const EntityListPanelList = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  gap: 'var(--entity-list-panel-gap-xs)',
  minHeight: 0,
  overflowY: 'auto',
  padding: '0 var(--entity-list-panel-spacing-xxs) 0 0',
  scrollbarGutter: 'stable',
  ...getPrismScrollbarStyles(theme, {
    thumbBorderColor: 'var(--prism-scrollbar-thumb-border-color)',
    thumbBorderWidth: 'var(--prism-scrollbar-thumb-border-width)',
    width: 'var(--prism-scrollbar-width)',
  }),
}));

export const EntityListPanelListFrame = styled('div')({
  display: 'flex',
  flex: '1 1 auto',
  minHeight: 0,
  minWidth: 0,
  position: 'relative',
});

export const EntityListPanelItemButton = styled(ButtonBase, {
  shouldForwardProp: shouldForwardItemButtonProp,
})<ItemButtonStyleProps>(({ isSelected, theme }) => ({
  alignItems: 'center',
  backgroundColor: isSelected ? theme.palette.brand[100] : theme.palette.background.paper,
  border: `var(--entity-list-panel-border-width) solid ${
    isSelected ? theme.palette.brand[500] : theme.palette.neutral[200]
  }`,
  borderRadius: 'var(--entity-list-panel-radius-xs)',
  boxSizing: 'border-box',
  color: isSelected ? theme.palette.brand[800] : theme.palette.neutral[500],
  display: 'grid',
  gap: 'var(--entity-list-panel-gap-xs)',
  gridTemplateColumns: 'minmax(0, 1fr) auto auto',
  height: 'var(--entity-list-panel-row-height)',
  minHeight: 'var(--entity-list-panel-row-height)',
  columnGap: 'var(--entity-list-panel-column-gap)',
  padding: 'var(--entity-list-panel-spacing-s)',
  textAlign: 'left',
  transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow', 'color'], {
    duration: theme.transitions.duration.shortest,
  }),
  width: '100%',
  '&:hover': {
    backgroundColor: theme.palette.brand[100],
    borderColor: isSelected ? theme.palette.brand[500] : theme.palette.neutral[200],
    color: theme.palette.brand[800],
  },
  '&:hover [data-entity-list-panel-badge="true"], &:focus-visible [data-entity-list-panel-badge="true"]': {
    borderColor: isSelected ? theme.palette.brand[600] : theme.palette.brand[400],
  },
  '&:focus-visible': {
    backgroundColor: theme.palette.brand[100],
    borderColor: isSelected ? theme.palette.brand[600] : theme.palette.brand[400],
    color: theme.palette.brand[800],
    outline: 0,
  },
  ...(isSelected && {
    '& [data-entity-list-panel-badge="true"]': {
      backgroundColor: theme.palette.brand[500],
      borderColor: theme.palette.brand[500],
      color: theme.palette.neutral.white,
    },
  }),
  '&.Mui-disabled': {
    color: theme.palette.text.disabled,
  },
}));

export const EntityListPanelSkeletonRow = styled('div')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.background.paper,
  border: `var(--entity-list-panel-border-width) solid ${theme.palette.neutral[200]}`,
  borderRadius: 'var(--entity-list-panel-radius-s)',
  boxSizing: 'border-box',
  display: 'grid',
  gap: 'var(--entity-list-panel-gap-xs)',
  gridTemplateColumns: 'minmax(0, 1fr) auto auto',
  height: 'var(--entity-list-panel-row-height)',
  minHeight: 'var(--entity-list-panel-row-height)',
  columnGap: 'var(--entity-list-panel-column-gap)',
  padding: 'var(--entity-list-panel-spacing-s)',
  '& > *:first-of-type': {
    maxWidth: '100%',
  },
}));

export const EntityListPanelItemLabel = styled('span')(({ theme }) => ({
  color: 'inherit',
  fontSize: 'var(--entity-list-panel-font-size-m)',
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: 'var(--entity-list-panel-line-height-m)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const EntityListPanelMeta = styled('div')(({ theme }) => ({
  alignItems: 'center',
  color: theme.palette.text.secondary,
  display: 'flex',
  gap: 'var(--entity-list-panel-gap-xs)',
  justifyContent: 'flex-end',
  maxWidth: 'var(--entity-list-panel-meta-max-width)',
  minWidth: 0,
  overflow: 'hidden',
}));

export const EntityListPanelBadge = styled('span')(({ theme }) => ({
  backgroundColor: theme.palette.brand[100],
  border: 'var(--entity-list-panel-border-width) solid transparent',
  borderRadius: 'var(--entity-list-panel-radius-xs)',
  boxSizing: 'border-box',
  color: theme.palette.neutral[600],
  display: 'block',
  flex: '0 1 auto',
  fontSize: 'var(--entity-list-panel-badge-font-size)',
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: 'var(--entity-list-panel-badge-line-height)',
  maxWidth: 'var(--entity-list-panel-badge-max-width)',
  minWidth: 0,
  overflow: 'hidden',
  padding: 'var(--entity-list-panel-spacing-xxs) var(--entity-list-panel-spacing-xs)',
  textOverflow: 'ellipsis',
  transition: theme.transitions.create(['background-color', 'border-color', 'color'], {
    duration: theme.transitions.duration.shortest,
  }),
  whiteSpace: 'nowrap',
  '&[data-entity-list-panel-badge-variant="overflow"]': {
    alignItems: 'center',
    display: 'inline-flex',
    flex: '0 0 auto',
    justifyContent: 'center',
    minWidth: 'var(--entity-list-panel-count-size)',
  },
}));

export const EntityListPanelEndContent = styled('span')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.neutral[200],
  borderRadius: 'var(--entity-list-panel-radius-xs)',
  color: theme.palette.neutral[800],
  display: 'inline-flex',
  flexShrink: 0,
  fontSize: 'var(--entity-list-panel-badge-font-size)',
  fontWeight: theme.typography.fontWeightLight,
  height: 'var(--entity-list-panel-count-size)',
  justifyContent: 'center',
  lineHeight: 'var(--entity-list-panel-badge-line-height)',
  minWidth: 'var(--entity-list-panel-count-size)',
  padding: '0 var(--entity-list-panel-spacing-xs)',
  textAlign: 'center',
  transition: theme.transitions.create(['background-color', 'color'], {
    duration: theme.transitions.duration.shortest,
  }),
  whiteSpace: 'nowrap',
}));

export const EntityListPanelState = styled('div')(({ theme }) => ({
  alignItems: 'center',
  color: theme.palette.text.secondary,
  display: 'grid',
  gap: 'var(--entity-list-panel-gap-xs)',
  justifyItems: 'center',
  padding: 'var(--entity-list-panel-state-padding-y) var(--entity-list-panel-state-padding-x)',
  textAlign: 'center',
}));

export const EntityListPanelStateTitle = styled('strong')(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: 'var(--entity-list-panel-font-size-s)',
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: 'var(--entity-list-panel-line-height-s)',
}));

export const EntityListPanelStateDescription = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 'var(--entity-list-panel-font-size-s)',
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: 'var(--entity-list-panel-line-height-s)',
}));
